import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Me, Property } from "../lib/api";

/**
 * Persistent chrome: which client you are looking at, and who you are
 * signed in as. Both were previously unreachable, which made a multi-client
 * workspace impossible to navigate and sign-out impossible to find.
 */
export function AppHeader({
  me, properties, activeId, onSelect, onAdd, onEdit, onDelete, demo, onToggleDemo,
}: {
  demo: boolean;
  onToggleDemo: () => void;
  me: Me | null;
  properties: Property[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onEdit: (p: Property) => void;
  onDelete: (p: Property) => void;
}) {
  const [propsOpen, setPropsOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const wrap = useRef<HTMLElement>(null);
  const propsTrigger = useRef<HTMLButtonElement>(null);
  const acctTrigger = useRef<HTMLButtonElement>(null);
  const propsPop = useRef<HTMLDivElement>(null);
  const acctPop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function away(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) {
        setPropsOpen(false); setAcctOpen(false);
      }
    }
    function esc(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      // Return focus to whichever trigger opened the menu.
      if (propsOpen) propsTrigger.current?.focus();
      if (acctOpen) acctTrigger.current?.focus();
      setPropsOpen(false); setAcctOpen(false);
    }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [propsOpen, acctOpen]);

  // Move focus into a menu when it opens, so the next Tab lands inside it.
  useEffect(() => {
    if (propsOpen) propsPop.current?.querySelector("button")?.focus();
  }, [propsOpen]);
  useEffect(() => {
    if (acctOpen) acctPop.current?.querySelector("button")?.focus();
  }, [acctOpen]);

  const active = properties.find((p) => p.id === activeId) ?? null;

  return (
    <header className="apphdr" ref={wrap}>
      <div className="apphdr-in">
        <span className="brand">Analytics</span>

        <div className="menu">
          <button ref={propsTrigger} className="menu-trigger"
                  onClick={() => { setPropsOpen(!propsOpen); setAcctOpen(false); }}
                  aria-expanded={propsOpen} aria-haspopup="menu">
            <span className="menu-trigger-label">
              {active ? active.name : "No client selected"}
            </span>
            <span className="caret" aria-hidden="true" />
          </button>

          {propsOpen && (
            <div className="menu-pop" role="menu" ref={propsPop}>
              <div className="menu-head">
                Clients<span className="num">{properties.length}</span>
              </div>
              {properties.length === 0 && (
                <p className="menu-empty">None yet. Add one to get started.</p>
              )}
              {properties.map((p) => (
                <div key={p.id} className={`menu-row ${p.id === activeId ? "on" : ""}`}>
                  <button className="menu-row-main" role="menuitem"
                          onClick={() => { onSelect(p.id); setPropsOpen(false); }}>
                    <span className="menu-row-name">{p.name}</span>
                    <span className="menu-row-sub num">
                      {p.sources.length
                        ? p.sources.map((s) => s.bq_dataset ?? s.kind).join(" · ")
                        : "no sources"}
                    </span>
                  </button>
                  <span className="menu-row-actions">
                    <button title="Edit sources"
                            onClick={() => { onEdit(p); setPropsOpen(false); }}>Edit</button>
                    <button title="Remove client" className="danger"
                            onClick={() => { onDelete(p); setPropsOpen(false); }}>Remove</button>
                  </span>
                </div>
              ))}
              <button className="menu-add" role="menuitem"
                      onClick={() => { onAdd(); setPropsOpen(false); }}>
                + Add a client
              </button>
            </div>
          )}
        </div>

        <button className={`demo-toggle ${demo ? "on" : ""}`}
                onClick={onToggleDemo}
                aria-pressed={demo}
                title={demo
                  ? "Showing generated sample data"
                  : "Explore the tools with generated sample data"}>
          <span className="demo-dot" aria-hidden="true" />
          Sample data
        </button>

        <div className="menu right">
          <button ref={acctTrigger} className="avatar"
                  onClick={() => { setAcctOpen(!acctOpen); setPropsOpen(false); }}
                  aria-expanded={acctOpen} aria-haspopup="menu"
                  title={me?.user.email ?? "Account"}>
            {(me?.user.email ?? "?").slice(0, 1).toUpperCase()}
          </button>

          {acctOpen && (
            <div className="menu-pop acct" role="menu" ref={acctPop}>
              <div className="acct-id">
                <div className="acct-email">{me?.user.email}</div>
                {me?.workspaces[0] && (
                  <div className="acct-ws num">
                    {me.workspaces[0].name} · {me.workspaces[0].role}
                  </div>
                )}
              </div>

              <div className="acct-block">
                <div className="acct-block-title">Google connections</div>
                {me?.connections.length
                  ? me.connections.map((c) => (
                      <div className="acct-conn" key={c.id}>
                        <span>{c.google_email}</span>
                        <span className={`pill ${c.status}`}>{c.status}</span>
                      </div>
                    ))
                  : <p className="menu-empty">None connected.</p>}
              </div>

              <button className="menu-add" role="menuitem"
                      onClick={() => supabase.auth.signOut()}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
